/**
 * useTreasury — hooks para el módulo de Tesorería (TES-01..04)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface FactoringEntity {
  id: string
  legal_name: string
  tax_id: string
  contact_email: string
  phone: string
  /** Va a <Cesionario><Direccion> del AEC: si está vacía el SII rechaza la cesión. */
  address: string
  notes: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export type FactoringOperationStatus = 'created' | 'processing' | 'accepted' | 'rejected' | 'paid'

export interface FactoringOperationLine {
  id: string
  sale_document: string
  sale_document_type: string | null
  sale_document_number: string | null
  customer_name: string | null
  amount_ceded: string
  identificador_cesion?: string
}

export interface FactoringPayment {
  id: string
  factoring_operation: string
  amount_received_net: string
  commissions_charged: string
  payment_date: string
  bank_account: string
  bank_account_name: string | null
  notes: string
  created_at: string
}

/**
 * Estado de la cesión ante el SII. Es un eje DISTINTO de `status` (que describe el
 * ciclo comercial: creada → cedida → liquidada): una operación puede estar "Cedida"
 * y tener la cesión rechazada por el RPETC. Lo deriva el backend a partir de la
 * glosa del SII — no interpretar `sii_estado` en el frontend.
 */
export type CesionStatus = 'not_sent' | 'pending' | 'accepted' | 'rejected'

/** Documento cedido, resumido para el listado (evita expandir para verlo). */
export interface FactoringOperationDocument {
  document_type: string
  folio: string | null
  customer_name: string | null
  amount_ceded: string
  identificador_cesion: string | null
}

export interface FactoringOperationListItem {
  id: string
  factoring_entity: string
  entity_name: string
  date: string
  status: FactoringOperationStatus
  total_amount_ceded: string
  line_count: number
  created_at: string
  created_by_name?: string | null
  sii_estado?: string
  cesion_status: CesionStatus
  cesion_status_label: string
  sii_track_id?: string
  sii_received_at?: string | null
  documents: FactoringOperationDocument[]
}

export interface FactoringOperation extends FactoringOperationListItem {
  lines: FactoringOperationLine[]
  accounting_voucher: string | null
  notes: string
  cesion_xml?: string
  cesion_signed?: boolean
  payment?: FactoringPayment
  // Registro RPETC del SII
  cesion_sent_at?: string | null
}

export interface FactoringReportRow {
  id: string
  date: string
  entity_name: string
  entity_tax_id: string
  status: FactoringOperationStatus
  total_amount_ceded: string
  line_count: number
  payment: {
    payment_date: string
    amount_received_net: string
    commissions_charged: string
  } | null
}

export interface FactoringReport {
  date_from: string | null
  date_to: string | null
  count: number
  total_amount_ceded: string
  total_amount_paid: string
  operations: FactoringReportRow[]
}

// ─── Query keys ────────────────────────────────────────────────────────────────

export const treasuryKeys = {
  entities:    ['treasury', 'entities'] as const,
  entity:      (id: string) => ['treasury', 'entities', id] as const,
  operations:  (params?: object) => ['treasury', 'operations', params] as const,
  operation:   (id: string) => ['treasury', 'operations', id] as const,
  report:      (params?: object) => ['treasury', 'report', params] as const,
}

// ─── TES-01: Factoring Entities ────────────────────────────────────────────────

export function useFactoringEntities(params?: { is_active?: string }) {
  return useQuery({
    queryKey: treasuryKeys.entities,
    queryFn: () =>
      apiClient.get<{ count: number; entities: FactoringEntity[] }>(
        '/treasury/factoring-entities/',
        { params }
      ).then(r => r.data),
  })
}

export function useCreateFactoringEntity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<FactoringEntity>) =>
      apiClient.post<FactoringEntity>('/treasury/factoring-entities/', data).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: treasuryKeys.entities }) },
  })
}

export function useUpdateFactoringEntity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<FactoringEntity> & { id: string }) =>
      apiClient.patch<FactoringEntity>(`/treasury/factoring-entities/${id}/`, data).then(r => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: treasuryKeys.entities })
      qc.invalidateQueries({ queryKey: treasuryKeys.entity(id) })
    },
  })
}

// ─── TES-02: Factoring Operations ─────────────────────────────────────────────

export function useFactoringOperations(params?: {
  entity_id?: string
  status?: string
  cesion_status?: string
  search?: string
  date_from?: string
  date_to?: string
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: treasuryKeys.operations(params),
    queryFn: () =>
      apiClient.get<{
        count: number
        page: number
        page_size: number
        num_pages: number
        operations: FactoringOperationListItem[]
      }>('/treasury/factoring-operations/', { params }).then(r => r.data),
  })
}

export function useFactoringOperation(id: string) {
  return useQuery({
    queryKey: treasuryKeys.operation(id),
    queryFn: () =>
      apiClient.get<FactoringOperation>(`/treasury/factoring-operations/${id}/`).then(r => r.data),
    enabled: !!id,
  })
}

export function useCreateFactoringOperation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      factoring_entity: string
      date: string
      notes?: string
      lines: { sale_document: string; amount_ceded: string }[]
    }) =>
      apiClient.post<FactoringOperation>('/treasury/factoring-operations/', data).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['treasury', 'operations'] }) },
  })
}

export function useSubmitFactoringOperation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<FactoringOperation>(`/treasury/factoring-operations/${id}/submit/`).then(r => r.data),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['treasury', 'operations'] })
      qc.invalidateQueries({ queryKey: treasuryKeys.operation(id) })
    },
  })
}

// ─── Cesión SII (RPETC): envío manual + registrar resultado ───────────────────

export function useSendCesion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      apiClient.post(`/treasury/factoring-operations/${id}/send-cesion/`, { force }).then(r => r.data),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['treasury', 'operations'] })
      qc.invalidateQueries({ queryKey: treasuryKeys.operation(id) })
    },
  })
}

export function useRecordSiiResult() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, raw }: { id: string; raw: string }) =>
      apiClient.post(`/treasury/factoring-operations/${id}/sii-result/`, { raw }).then(r => r.data),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['treasury', 'operations'] })
      qc.invalidateQueries({ queryKey: treasuryKeys.operation(id) })
    },
  })
}

/**
 * Consulta al Registro del SII si la cesión quedó anotada. Es la fuente confiable:
 * el correo del SII puede no llegar nunca. La misma consulta corre automáticamente
 * 10 minutos después del envío.
 */
export function useConsultarCesionEnSii() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ ok: boolean; encontradas: number; throttled?: boolean; detalle?: string }>(
        `/treasury/factoring-operations/${id}/consultar-sii/`,
      ).then(r => r.data),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['treasury', 'operations'] })
      qc.invalidateQueries({ queryKey: treasuryKeys.operation(id) })
    },
  })
}

// ─── TES-03: Register Payment ─────────────────────────────────────────────────

export function useRegisterFactoringPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string
      amount_received_net: string
      commissions_charged?: string
      payment_date: string
      bank_account: string
      notes?: string
    }) =>
      apiClient.post<FactoringPayment>(
        `/treasury/factoring-operations/${id}/register-payment/`, data
      ).then(r => r.data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['treasury', 'operations'] })
      qc.invalidateQueries({ queryKey: treasuryKeys.operation(id) })
    },
  })
}

// ─── TES-04: Report ───────────────────────────────────────────────────────────

export function useFactoringReport(params?: {
  date_from?: string
  date_to?: string
  entity_id?: string
  status?: string
} | null) {
  return useQuery({
    queryKey: treasuryKeys.report(params ?? {}),
    queryFn: () =>
      apiClient.get<FactoringReport>('/treasury/factoring-operations/report/', { params: params ?? {} }).then(r => r.data),
    enabled: params !== null,
  })
}
